import Delta from "quill-delta";
import { dataUriToBlob } from "../adapters/dataUriToBlob";
import { registerPendingImage } from "../adapters/pendingImages";

type Params = {
  allowImages: boolean;
  /**
   * Whether we are inside a genuine user paste/drop. Quill runs clipboard
   * matchers on the programmatic load path too — react-quill seeds the editor
   * by handing stored HTML to `clipboard.convert()` — so without this gate the
   * matcher would act on content the user never touched.
   */
  isUserInsert: boolean;
};

/**
 * Quill clipboard matcher for <img> elements. Inside a user paste/drop:
 * without the image control the image is dropped entirely (rich text fields
 * like approve/reject messages must never carry images); with it, data: URIs
 * are converted to in-memory blobs so base64 never reaches the persisted
 * HTML. http(s) srcs pass through.
 */
export const createImageMatcher = ({ allowImages, isUserInsert }: Params) => (
  node: HTMLImageElement,
  delta: Delta
): Delta => {
  if (!isUserInsert) {
    // Load path, not an insert. Converting here would re-upload a legacy
    // stored base64 image every single time the form is opened (a brand new
    // storage file per open), and stripping here would silently delete
    // already-saved content. Leave it exactly as stored.
    return delta;
  }
  if (!allowImages) {
    return new Delta();
  }
  const src = node.getAttribute("src") ?? "";
  if (!src.startsWith("data:image/")) {
    return delta;
  }
  const decoded = dataUriToBlob(src);
  if (!decoded) {
    // Un-decodable inline payload (non-base64 data: URI, e.g.
    // `data:image/svg+xml;utf8,...`, or corrupted base64) — we have no way
    // to persist this as a real image, so treat it exactly like the
    // !allowImages case: never let it reach the document.
    return new Delta();
  }
  const pendingSrc = registerPendingImage({ ...decoded, scale: 100 });
  return new Delta().insert({ image: pendingSrc });
};
